import type { IHttpClient } from "@/core/interfaces";
import { isRecord } from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";

import type {
  AlterarCotacaoBody,
  CotacaoObra,
  CotacaoReferencia,
  DefinirCotacaoBody,
  SolicitacaoCotacao,
  SolicitacaoCotacaoCancelada,
  SolicitarAlteracaoCotacaoBody,
} from "./financeiro-obras.cotacao.types";
import { cabecalhoIdempotencia, readEnvelopeData } from "./financeiro-obras.gateway";
import type { LookupRequestOptions, OpcoesMutacao } from "./financeiro-obras.gateway";

export const FINANCEIRO_COTACAO_PATHS = {
  REFERENCIA: (moedaId: string) =>
    `/api/financeiro/moedas/${encodeURIComponent(moedaId)}/cotacao-referencia`,
  DEFINIR: (obraId: string) => `/api/financeiro/obras/${encodeURIComponent(obraId)}/cotacoes`,
  ALTERAR: (obraId: string, moedaId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/cotacoes/${encodeURIComponent(moedaId)}`,
  SOLICITAR_ALTERACAO: (obraId: string, moedaId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/cotacoes/${encodeURIComponent(moedaId)}/solicitacoes-alteracao`,
  CANCELAR_SOLICITACAO: (obraId: string, moedaId: string, solicitacaoId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/cotacoes/${encodeURIComponent(moedaId)}/solicitacoes-alteracao/${encodeURIComponent(solicitacaoId)}/cancelamento`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

export async function getCotacaoReferencia(
  moedaId: string,
  { signal }: LookupRequestOptions = {},
): Promise<CotacaoReferencia> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_COTACAO_PATHS.REFERENCIA(moedaId),
    { signal },
  );
  const payload = response.data;
  if (isRecord(payload) && "data" in payload && payload.data === null) return null;
  return readEnvelopeData<NonNullable<CotacaoReferencia>>(payload);
}

export async function definirCotacaoObra(
  obraId: string,
  body: DefinirCotacaoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<CotacaoObra> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_COTACAO_PATHS.DEFINIR(obraId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<CotacaoObra>(response.data);
}

export async function alterarCotacaoObra(
  obraId: string,
  moedaId: string,
  body: AlterarCotacaoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<CotacaoObra> {
  const response = await getHttpClient().put<unknown>(
    FINANCEIRO_COTACAO_PATHS.ALTERAR(obraId, moedaId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<CotacaoObra>(response.data);
}

export async function solicitarAlteracaoCotacao(
  obraId: string,
  moedaId: string,
  body: SolicitarAlteracaoCotacaoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<SolicitacaoCotacao> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_COTACAO_PATHS.SOLICITAR_ALTERACAO(obraId, moedaId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<SolicitacaoCotacao>(response.data);
}

export async function cancelarSolicitacaoCotacao(
  obraId: string,
  moedaId: string,
  solicitacaoId: string,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<SolicitacaoCotacaoCancelada> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_COTACAO_PATHS.CANCELAR_SOLICITACAO(obraId, moedaId, solicitacaoId),
    undefined,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<SolicitacaoCotacaoCancelada>(response.data);
}
